/* BackupAction.js */

"use strict";

const BackupDetailComponent = require("./veils/backupdetail/BackupDetailComponent");
const BackupSummaryComponent = require("./veils/backupsummary/BackupSummaryComponent");
const _ = require("lodash");

class BackupAction {
    constructor(
        logger,
        gameEditor,
        modalUserFeedback,
        DeviceEntity,
        userSession,
        activeView,
        ValueMap
    ) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._deviceEntity = DeviceEntity;
        this._modalUserFeedback = modalUserFeedback;
        this._activeView = activeView;
        this._valueMap = ValueMap;
        this._selectedDeviceIds = _.get(this._activeView, "pagingFeed", {})
            ? this._activeView.getSelectedDeviceIds().selectedRows || []
            : [];
        this.userSession = userSession;
        this.run = this.run.bind(this);
        this._backupDevices = this._backupDevices.bind(this);
    }

    async run() {
        if (this._selectedDeviceIds.length) {
            try {
                await this._activeView.pagingFeed.waitForRangeIdsUpdated();

                const query = {
                    id: {$in: this._selectedDeviceIds},
                };

                const devices = await this._activeView._DeviceEntityDef.loadDevices(
                    this._activeView.activeOrgId,
                    query,
                    {
                        id: true,
                        name: true,
                        detectedOs: true,
                        detectedVendor: true,
                    }
                );

                this._selectedDeviceNames = devices.map((device) => ({
                    name: device.name,
                    vendor: device.detectedVendor,
                    os: device.detectedOs,
                }));

                let message = "";
                const {
                    name,
                    runCapture,
                    query: backupQuery,
                } = await this._modalUserFeedback.displayCustom(
                    new BackupDetailComponent(
                        this._logger,
                        this,
                        message,
                        this._selectedDeviceNames,
                        this._valueMap
                    ),
                    {
                        dontHide: true,
                    }
                );

                await this._backupDevices(name, runCapture, backupQuery);
            } catch (err) {
                if (err && err.userRejected) {
                    this._logger.warn({error: err}, "Backup action - User Canceled");
                    this._modalUserFeedback.hideDisplayed();
                } else {
                    this._logger.warn({error: err}, "Backup action - Error");
                    await this._modalUserFeedback.displayError(err);
                }
            }
        } else {
            return Promise.resolve();
        }
    }

    async _backupDevices(name, runCapture, query) {
        try {
            const workInput = {
                description:
                    name ||
                    `Backup - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
                runCapture: runCapture,
            };

            const workTarget = {
                category: "entity",
                entityType: "Device",
                method: "query",
                query,
            };

            const progressCallBack = ({workId, successCount, failureCount, targetCount}) => {
                if (
                    _.isNumber(successCount) &&
                    _.isNumber(failureCount) &&
                    _.isNumber(targetCount)
                ) {
                    const msg = `Succeeded ${successCount}, failed ${failureCount} of ${targetCount} devices`;
                    this._modalUserFeedback.showLoadingProgress(msg);
                }
            };

            this._modalUserFeedback.displayLoading({
                message: "Backing up your devices...",
                showHideButton: true,
            });

            const result = await this._deviceEntity.runBackupWork(
                this.userSession.activeOrganization.id,
                workTarget,
                workInput,
                {launchAndForget: false},
                progressCallBack
            );

            if (!this._modalUserFeedback.isStillOpen || result.type === "schedule") {
                return this._modalUserFeedback.hideDisplayed();
            }

            if (result.errorMsg) {
                const err = new Error(result.errorMsg);
                err.errorCode = "CaptureError";
                err.errorMsg = result.errorMsg;
                throw err;
            }

            return this._modalUserFeedback.displayCustom(
                new BackupSummaryComponent(result, this._logger)
            );
        } catch (err) {
            if (err.userRejected || err.closed) {
                this._logger.warn({error: err}, "ImportAction.run() - User Canceled.");
                return this._modalUserFeedback.hideDisplayed();
            } else {
                this._logger.warn({error: err}, "ImportAction.run() - Error");
                await this._modalUserFeedback.displayError(err);
            }
        }
    }
}

module.exports = BackupAction;

BackupAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "$teve.entities.Device",
    "$teve.userSession",
    "gameEditor.activeView",
    "$teve.entities.ValueMap",
];

BackupAction.$displayDetails = {
    type: "Action",
    name: "BackupAction",
    icon: "common.backup",
    title: "Backup devices",
    shown: true,
    order: 9,
};
